import { motion, AnimatePresence } from 'framer-motion';
import { X, Shapes, RefreshCw, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import { getMolduraImageUrl } from '@/utils/image-utils';

interface MoldurasModalProps {
    show: boolean;
    onClose: () => void;
    /** Molduras da página atual (já fatiadas no pai). */
    pageItems: any[];
    page: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    /** Carregamento do catálogo completo (uma requisição). */
    loading: boolean;
    selectedMolId: number | null;
    onSelect: (moldura: any) => void;
}

export function MoldurasModal({
    show,
    onClose,
    pageItems,
    page,
    totalPages,
    onPageChange,
    loading,
    selectedMolId,
    onSelect,
}: MoldurasModalProps) {
    return (
        <AnimatePresence>
            {show && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
                    onClick={onClose}
                >
                    <motion.div
                        initial={{ scale: 0.95, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.95, opacity: 0, y: 20 }}
                        onClick={(e) => e.stopPropagation()}
                        className="bg-neutral-900 w-full max-w-2xl rounded-[2.5rem] border border-white/10 shadow-2xl flex flex-col max-h-[85vh] overflow-hidden"
                    >
                        <div className="p-8 border-b border-white/5 flex justify-between items-center flex-shrink-0 bg-neutral-900/50 backdrop-blur-xl">
                            <div>
                                <h3 className="text-xl font-black text-white flex items-center gap-3 uppercase tracking-tight">
                                    <Shapes size={24} className="text-orange-500" /> Galeria de Molduras
                                </h3>
                                <p className="text-white/40 text-[10px] font-black uppercase tracking-[0.2em] mt-1">Personalize o destaque do seu perfil</p>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2.5 hover:bg-white/5 rounded-full transition-all text-white/40 hover:text-white border border-white/10 active:scale-90"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="flex flex-col flex-1 min-h-0">
                            {loading ? (
                                <div className="flex flex-col items-center justify-center py-20 gap-6 flex-1">
                                    <div className="relative">
                                        <div className="w-16 h-16 rounded-full border-2 border-orange-500/20 animate-ping absolute inset-0"></div>
                                        <RefreshCw className="animate-spin text-orange-500 relative z-10" size={64} strokeWidth={1.5} />
                                    </div>
                                    <span className="text-white/20 font-black uppercase tracking-[0.3em] text-[10px]">A carregar molduras…</span>
                                </div>
                            ) : (
                                <>
                                    <div className="px-8 pt-6 pb-4 flex-shrink-0 border-b border-white/5">
                                        <button
                                            type="button"
                                            onClick={() => onSelect(null)}
                                            className={`w-full group relative rounded-2xl border-2 flex items-center gap-4 px-4 py-3 transition-all duration-300 overflow-hidden ${selectedMolId === null
                                                ? 'border-orange-500 bg-orange-500/10 shadow-[0_0_24px_rgba(249,115,22,0.12)]'
                                                : 'border-white/5 bg-white/5 hover:border-white/20 hover:bg-white/[0.07]'
                                                }`}
                                        >
                                            <div className="flex h-12 w-12 shrink-0 items-center justify-center text-white/20 group-hover:text-white/40">
                                                <X size={28} strokeWidth={1} />
                                            </div>
                                            <span className={`text-[11px] font-black uppercase tracking-widest ${selectedMolId === null ? 'text-orange-500' : 'text-white/40'}`}>
                                                Sem moldura
                                            </span>
                                            {selectedMolId === null && (
                                                <div className="ml-auto bg-orange-500 rounded-full p-1 shadow-lg">
                                                    <Check size={12} className="text-white" strokeWidth={3} />
                                                </div>
                                            )}
                                        </button>
                                    </div>

                                    {totalPages > 1 && (
                                        <div className="flex items-center justify-between gap-3 px-8 py-3 border-b border-white/5 flex-shrink-0">
                                            <button
                                                type="button"
                                                disabled={page <= 1}
                                                onClick={() => onPageChange(page - 1)}
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/80 transition-all hover:border-orange-500/40 hover:bg-white/10 disabled:pointer-events-none disabled:opacity-30"
                                                aria-label="Página anterior"
                                            >
                                                <ChevronLeft size={20} />
                                            </button>
                                            <span className="text-[10px] font-black uppercase tracking-widest text-white/35">
                                                Página {page} de {totalPages}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={page >= totalPages}
                                                onClick={() => onPageChange(page + 1)}
                                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] text-white/80 transition-all hover:border-orange-500/40 hover:bg-white/10 disabled:pointer-events-none disabled:opacity-30"
                                                aria-label="Página seguinte"
                                            >
                                                <ChevronRight size={20} />
                                            </button>
                                        </div>
                                    )}

                                    <div className="p-8 overflow-y-auto flex-1 min-h-0 no-scrollbar">
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
                                            {pageItems.map((moldura) => {
                                                const isSelected = moldura.mol_id === selectedMolId;
                                                return (
                                                    <button
                                                        key={moldura.mol_id}
                                                        type="button"
                                                        onClick={() => onSelect(moldura)}
                                                        className={`group relative aspect-square rounded-[2rem] border-2 flex flex-col items-center justify-center gap-3 transition-all duration-500 overflow-hidden ${isSelected
                                                            ? 'border-orange-500 bg-orange-500/10 shadow-[0_0_40px_rgba(249,115,22,0.15)]'
                                                            : 'border-white/5 bg-white/5 hover:border-white/20 hover:bg-white/[0.07]'
                                                            }`}
                                                    >
                                                        <div className="relative w-20 h-20 md:w-24 md:h-24 flex items-center justify-center p-2">
                                                            <div className="relative w-full h-full">
                                                                <img
                                                                    src={getMolduraImageUrl(moldura.mol_imagem) || ''}
                                                                    className="w-full h-full object-contain relative z-10 transition-transform duration-500 group-hover:scale-110"
                                                                    alt={moldura.mol_nome}
                                                                    loading="lazy"
                                                                    decoding="async"
                                                                />
                                                                <div className="absolute inset-0 bg-orange-500/20 blur-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                                            </div>
                                                        </div>

                                                        <span className={`text-[10px] font-black uppercase tracking-widest px-2 truncate w-full text-center ${isSelected ? 'text-orange-500' : 'text-white/30 group-hover:text-white/60'
                                                            }`}>
                                                            {moldura.mol_nome}
                                                        </span>

                                                        {isSelected && (
                                                            <div className="absolute top-3 right-3 bg-orange-500 rounded-full p-1 shadow-lg">
                                                                <Check size={12} className="text-white" strokeWidth={3} />
                                                            </div>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {pageItems.length === 0 && (
                                            <p className="text-center text-sm text-white/40 py-12">Nenhuma moldura nesta página.</p>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
